<div class="members-page">
  <app-workspace-hub-nav></app-workspace-hub-nav>

  <section class="members-hero">
    <div>
      <p class="members-kicker">People and access</p>
      <h1>Members</h1>
      <p>Manage workspace membership, invitations, roles, Teams licences, and access outside supported clients.</p>
    </div>
    @if (this.contextState() === 'ready') {
      <button type="button" class="members-primary" [disabled]="!this.canInvite()" (click)="this.inviteOpen.set(true)">+ Add member</button>
    }
  </section>

  @if (this.contextState() === 'loading') {
    <section class="members-state">Loading workspace members…</section>
  }
  @if (this.contextState() === 'noStorage') {
    <section class="members-state members-state--warning">Workspace storage must be provisioned before members can be managed.</section>
  }
  @if (this.contextState() === 'noLicense') {
    <section class="members-state members-state--error">A Flow Studio Teams licence is required.</section>
  }
  @if (this.contextState() === 'adminRequired') {
    <section class="members-state">
      <strong>Administrator access required</strong>
      <p>Only workspace owners and admins can view or manage the member directory.</p>
    </section>
  }
  @if (this.contextState() === 'error') {
    <section class="members-state members-state--error">
      <strong>Members could not be loaded</strong>
      <p>{{ this.errorMessage() }}</p>
      <button type="button" class="members-secondary" (click)="this.loadMembers()">Try again</button>
    </section>
  }

  @if (this.contextState() === 'ready') {
    @if (this.memberSource() === 'legacy') {
      <div class="members-inline-note">This workspace uses existing Teams membership. Pooled access controls will appear here after workspace migration.</div>
    }
    <section class="members-stats">
      <article><span>Active members</span><strong>{{ this.activeCount() | number }}</strong><small>{{ this.callerRole() | titlecase }} access</small></article>
      <article><span>Teams seats</span><strong>{{ this.seatsUsed() | number }} / {{ (this.seatLimit() ?? '—') }}</strong><small>Active and pending members</small></article>
      <article><span>Pending invitations</span><strong>{{ this.pendingCount() | number }}</strong><small>Awaiting first sign-in</small></article>
      <article><span>External members</span><strong>{{ this.externalCount() | number }}</strong><small>Outside the owning tenant</small></article>
    </section>

    @if (this.errorMessage()) {
      <div class="members-inline-error" role="alert">{{ this.errorMessage() }}</div>
    }

    @if (this.inviteOpen()) {
      <section class="members-invite" aria-label="Invite workspace member">
        <div>
          <h2>Invite member</h2>
          <p>An activation email will be sent to the new member.</p>
        </div>
        <label>Email<input type="email" [(ngModel)]="this.inviteEmail" placeholder="name@company.com" /></label>
        <label>Role
          <select [(ngModel)]="this.inviteRole">
            <option value="user">User</option>
            @if (this.callerRole() === 'owner' || this.memberSource() === 'legacy') { <option value="admin">Admin</option> }
          </select>
        </label>
        <label class="members-check"><input type="checkbox" [(ngModel)]="this.inviteOutsideCowork" /> Allow access outside supported clients</label>
        <div class="members-invite-actions">
          <button type="button" class="members-secondary" (click)="this.inviteOpen.set(false)">Cancel</button>
          <button type="button" class="members-primary" [disabled]="!this.inviteEmail.trim() || this.savingInvite()" (click)="this.inviteMember()">{{ this.savingInvite() ? 'Sending…' : 'Send invitation' }}</button>
        </div>
      </section>
    }

    <section class="members-card">
      <div class="members-toolbar">
        <div class="members-search"><span aria-hidden="true">⌕</span><input type="search" placeholder="Search members or email…" [value]="this.search()" (input)="this.updateSearch($any($event.target).value)" /></div>
        <select aria-label="Filter by role" [value]="this.roleFilter()" (change)="this.updateRoleFilter($any($event.target).value)"><option value="all">All roles</option><option value="owner">Owner</option><option value="admin">Admin</option><option value="user">User</option></select>
        <select aria-label="Filter by status" [value]="this.statusFilter()" (change)="this.updateStatusFilter($any($event.target).value)"><option value="all">All statuses</option><option value="active">Active</option><option value="pending">Pending</option><option value="disabled">Disabled</option></select>
        <span class="members-result-count">{{ this.filteredMembers().length }} shown</span>
      </div>
      <div class="members-table-wrap">
        <table>
          <thead><tr><th>Member</th><th>Role</th><th>Teams licence</th><th>External</th><th>Outside client</th><th>Status</th><th><span class="sr-only">Actions</span></th></tr></thead>
          <tbody>
            @for (member of this.filteredMembers(); track this.trackMember($index, member)) {
              <tr [class.is-disabled]="member.disabled">
                <td><strong>{{ member.email }}</strong><small>{{ member.addedTime ? ('Added ' + (member.addedTime | date:'mediumDate')) : 'Workspace member' }}</small></td>
                <td>
                  @if (this.canSetRole(member)) {
                    <select [value]="member.role" [disabled]="this.mutatingRowKey() === member.rowKey" (change)="this.changeRole(member, $any($event.target).value)"><option value="user">User</option><option value="admin">Admin</option></select>
                  } @else { <span class="members-pill members-pill--blue">{{ member.role | titlecase }}</span> }
                </td>
                <td><span class="members-pill" [class.members-pill--green]="this.licenseLabel(member) === 'Assigned'">{{ this.licenseLabel(member) }}</span></td>
                <td>{{ member.external === null ? 'Pending' : (member.external ? 'Yes' : 'No') }}</td>
                <td>
                  @if (this.memberSource() === 'legacy') { <span title="Available after workspace migration">Coming soon</span> }
                  @else { <input type="checkbox" [checked]="member.allowedOutsideCowork" [disabled]="!this.canManageMember(member) || this.mutatingRowKey() === member.rowKey" (change)="this.changeAccess(member, $any($event.target).checked)" [attr.aria-label]="'Allow ' + member.email + ' outside supported clients'" /> }
                </td>
                <td><span class="members-pill" [class.members-pill--green]="!member.disabled && member.status === 'active'" [class.members-pill--amber]="!member.disabled && member.status === 'pending'">{{ member.disabled ? 'Disabled' : (member.status | titlecase) }}</span></td>
                <td><button type="button" class="members-remove" [disabled]="!this.canManageMember(member) || this.mutatingRowKey() === member.rowKey" (click)="this.removeMember(member)">Remove</button></td>
              </tr>
            } @empty {
              <tr><td colspan="7" class="members-empty">No members match the current filters.</td></tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  }
</div>
